<script setup>
import { computed, onActivated, ref } from 'vue';
import { picoSearch } from '@chatwoot/pico-search';
import { useI18n } from 'vue-i18n';

import AiAgentsAPI from 'dashboard/api/aiAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import AiAgentForm from './AiAgentForm.vue';

const { t } = useI18n();
const store = useStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const agents = ref([]);
const isLoading = ref(true);
const searchQuery = ref('');
const isDeletingId = ref(null);
const showDeletePopup = ref(false);
const selectedAgent = ref(null);
const formRef = ref(null);

const filteredAgents = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agents.value;
  return picoSearch(agents.value, query, ['name']);
});

const inboxName = agent =>
  inboxes.value.find(inbox => inbox.id === agent.inbox_id)?.name ||
  t('AI_AGENT.LIST.ALL_INBOXES');

const fetchAgents = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentsAPI.getAgents();
    agents.value = Array.isArray(data) ? data : [];
  } catch {
    useAlert(t('AI_AGENT.LIST.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openForm = agent => formRef.value?.open(agent);

const openDeletePopup = agent => {
  selectedAgent.value = agent;
  showDeletePopup.value = true;
};

const closeDeletePopup = () => {
  showDeletePopup.value = false;
  selectedAgent.value = null;
};

const confirmDeletion = async () => {
  const agent = selectedAgent.value;
  if (!agent) return;
  isDeletingId.value = agent.id;
  closeDeletePopup();
  try {
    await AiAgentsAPI.deleteAgent(agent.id);
    agents.value = agents.value.filter(item => item.id !== agent.id);
  } catch {
    useAlert(t('AI_AGENT.LIST.DELETE_ERROR'));
  } finally {
    isDeletingId.value = null;
  }
};

onActivated(async () => {
  await Promise.all([
    store.dispatch('inboxes/get'),
    store.dispatch('labels/get'),
  ]);
  fetchAgents();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AI_AGENT.LIST.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AI_AGENT.LIST.TITLE')"
        :description="$t('AI_AGENT.LIST.DESCRIPTION')"
        :search-placeholder="$t('AI_AGENT.LIST.SEARCH_PLACEHOLDER')"
      >
        <template v-if="agents.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AI_AGENT.LIST.COUNT', { n: agents.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AI_AGENT.LIST.NEW')"
            icon="i-lucide-plus"
            size="sm"
            @click="openForm()"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!agents.length"
        class="flex flex-col items-center justify-center gap-3 py-20 text-center"
      >
        <span class="i-lucide-bot text-4xl text-n-slate-9" />
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('AI_AGENT.LIST.EMPTY_TITLE') }}
        </h2>
        <p class="max-w-md mb-0 text-sm text-n-slate-11">
          {{ $t('AI_AGENT.LIST.EMPTY_DESCRIPTION') }}
        </p>
        <Button
          :label="$t('AI_AGENT.LIST.NEW')"
          size="sm"
          @click="openForm()"
        />
      </div>

      <div
        v-else-if="!filteredAgents.length"
        class="flex items-center justify-center py-20"
      >
        <span class="text-base text-n-slate-11">
          {{ $t('AI_AGENT.LIST.NO_RESULTS') }}
        </span>
      </div>

      <ul v-else class="m-0 divide-y list-none divide-n-weak">
        <li
          v-for="agent in filteredAgents"
          :key="agent.id"
          class="flex items-center gap-3 py-3"
        >
          <div class="flex-1 min-w-0">
            <button
              class="p-0 text-sm font-medium truncate bg-transparent text-n-slate-12 hover:underline"
              @click="openForm(agent)"
            >
              {{ agent.name }}
            </button>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ inboxName(agent) }}
              ·
              {{
                agent.active
                  ? $t('AI_AGENT.LIST.ACTIVE')
                  : $t('AI_AGENT.LIST.INACTIVE')
              }}
              ·
              {{
                $t('AI_AGENT.LIST.DOCUMENTS_COUNT', {
                  n: (agent.documents || []).length,
                })
              }}
            </p>
          </div>
          <div class="flex items-center gap-1">
            <Button
              v-tooltip.top="$t('AI_AGENT.LIST.EDIT')"
              icon="i-lucide-pen"
              color="slate"
              size="sm"
              @click="openForm(agent)"
            />
            <Button
              v-tooltip.top="$t('AI_AGENT.LIST.DELETE')"
              icon="i-woot-bin"
              color="slate"
              size="sm"
              :is-loading="isDeletingId === agent.id"
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDeletePopup(agent)"
            />
          </div>
        </li>
      </ul>

      <p
        v-if="filteredAgents.length"
        class="flex items-center gap-1 mt-3 mb-0 text-xs text-n-slate-11"
      >
        <Icon icon="i-lucide-info" class="size-3.5" />
        {{ $t('AI_AGENT.LIST.HANDOFF_HINT') }}
      </p>

      <AiAgentForm
        ref="formRef"
        :inboxes="inboxes"
        :labels="labels"
        @saved="fetchAgents"
      />

      <woot-delete-modal
        v-model:show="showDeletePopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('AI_AGENT.LIST.DELETE_TITLE')"
        :message="$t('AI_AGENT.LIST.DELETE_CONFIRM')"
        :message-value="selectedAgent ? ` ${selectedAgent.name}?` : ''"
        :confirm-text="$t('AI_AGENT.LIST.DELETE_YES')"
        :reject-text="$t('AI_AGENT.LIST.DELETE_NO')"
      />
    </template>
  </SettingsLayout>
</template>
